import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as mammoth from 'mammoth';

import useStyle from './style';

import Loading from 'components/loading';

interface Props {
  path: string;
  name: string;
}

const DocViewer: React.FC<Props> = ({ path, name }) => {
  const classes = useStyle();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    if (!path) return;
    let cancelled = false;

    async function fetchDoc(): Promise<void> {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(path, {
          responseType: 'arraybuffer',
          external: true,
        });

        if (cancelled) return;

        const arrayBuffer = response as any;

        // convert images to data URIs so they render in the browser
        const options: any = {
          convertImage: mammoth.images.imgElement(function (image: any) {
            return image.read('base64').then(function (imageBuffer: string) {
              return { src: `data:${image.contentType};base64,${imageBuffer}` };
            });
          }),
        };

        const result = await (mammoth as any).convertToHtml({ arrayBuffer }, options);
        if (cancelled) return;
        setHtml(result?.value ?? null);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load document');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchDoc();

    return () => {
      cancelled = true;
    };
  }, [path]);

  if (loading) return <Loading loading testId={`doc-viewer-loader-${name}`} />;
  if (error) return <div className={classes.mainContainer}>{error}</div>;
  if (!html) return <div className={classes.mainContainer}>No document to display</div>;

  return (
    <div className={classes.mainContainer} data-testid="docx-viewer">
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
};

export default DocViewer;
